import React, { useState, useEffect } from 'react';
import { emailAnalyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Mail, Send, Eye, MousePointer, AlertTriangle, MessageSquare, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';

export default function EmailAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await emailAnalyticsAPI.getAnalytics(); setData(res.data); }
      catch { toast.error('Failed to load email analytics'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="email-analytics-page">
      <div><h1 className="text-2xl font-bold tracking-tight">Email Sequence Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">Track delivery, open rates, clicks, and sequence performance</p></div>

      {/* Delivery Funnel */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <MetricCard icon={Send} label="Sent" value={s.total_sent} color="text-blue-500" />
        <MetricCard icon={Mail} label="Delivered" value={`${s.delivery_rate}%`} sub={s.delivered} color="text-emerald-500" />
        <MetricCard icon={Eye} label="Open Rate" value={`${s.open_rate}%`} color="text-amber-500" />
        <MetricCard icon={MousePointer} label="Click Rate" value={`${s.click_rate}%`} color="text-purple-500" />
        <MetricCard icon={AlertTriangle} label="Bounce Rate" value={`${s.bounce_rate}%`} color="text-red-500" />
        <MetricCard icon={MessageSquare} label="Reply Rate" value={`${s.reply_rate}%`} color="text-cyan-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily Trend */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Daily Email Activity</CardTitle></CardHeader>
          <CardContent>
            {(data?.daily_trend || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={data.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="sent" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.1} name="Sent" />
                  <Area type="monotone" dataKey="opened" fill="#f59e0b" stroke="#f59e0b" fillOpacity={0.1} name="Opened" />
                  <Area type="monotone" dataKey="clicked" fill="#10b981" stroke="#10b981" fillOpacity={0.1} name="Clicked" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No email activity data available yet</p>}
          </CardContent>
        </Card>

        {/* Delivery Funnel Visual */}
        <Card>
          <CardHeader><CardTitle className="text-sm">Email Delivery Funnel</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="email-funnel">
              {[
                { label: 'Sent', count: s.total_sent || 0, pct: 100, color: '#3b82f6' },
                { label: 'Delivered', count: s.delivered || 0, pct: s.delivery_rate || 0, color: '#10b981' },
                { label: 'Opened', count: Math.round((s.total_sent || 0) * (s.open_rate || 0) / 100), pct: s.open_rate || 0, color: '#f59e0b' },
                { label: 'Clicked', count: Math.round((s.total_sent || 0) * (s.click_rate || 0) / 100), pct: s.click_rate || 0, color: '#8b5cf6' },
                { label: 'Replied', count: Math.round((s.total_sent || 0) * (s.reply_rate || 0) / 100), pct: s.reply_rate || 0, color: '#06b6d4' },
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-20 text-xs font-medium text-right text-muted-foreground">{step.label}</div>
                  <div className="flex-1">
                    <div className="h-7 rounded-md flex items-center px-2 text-white text-xs font-bold"
                      style={{ width: `${Math.max(step.pct, 5)}%`, backgroundColor: step.color }}>
                      {step.count}
                    </div>
                  </div>
                  <div className="w-12 text-xs text-muted-foreground text-right">{step.pct}%</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sequence Performance */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Sequence Performance</CardTitle></CardHeader>
        <CardContent>
          {(data?.sequences || []).length > 0 ? (
            <div className="space-y-2">
              {data.sequences.map((seq, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50" data-testid={`seq-row-${i}`}>
                  <div><p className="text-sm font-medium">{seq.name}</p>
                    <p className="text-xs text-muted-foreground">{seq.total_sent} emails sent</p></div>
                  <div className="flex items-center gap-4 text-sm">
                    <span><Eye className="h-3 w-3 inline mr-1 text-amber-500" />{seq.open_rate}%</span>
                    <span><MousePointer className="h-3 w-3 inline mr-1 text-purple-500" />{seq.click_rate}%</span>
                    <Badge variant={seq.status === 'active' ? 'default' : 'secondary'}>{seq.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground text-center py-8">No email sequences configured yet. Email data will appear here as sequences run.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card><CardContent className="pt-3 pb-3">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <div><p className="text-lg font-bold">{value}</p><p className="text-[10px] text-muted-foreground">{label}</p>
          {sub !== undefined && <p className="text-[10px] text-muted-foreground/70">{sub} total</p>}</div>
      </div>
    </CardContent></Card>
  );
}
